<template>
  <div
    v-if="tabs.length > 0"
    class="downloads-module"
    data-cy="downloads-module"
  >
    <UITabContainer :tabs="tabs">
      <template v-slot:LocPickup>
        <LocPickupGrid v-if="main.locPickup" :items="main.locPickup" />
      </template>
      <template v-slot:Downloads>
        <div class="downloads-module__container" data-cy="downloads-content">
          <div
            v-if="isAuthorizedForLocDownloads"
            class="downloads-module__flex"
          >
            <Button
              v-if="inHouseLocAvailable"
              class="downloads-module__download-button"
              themeColor="primary"
              type="button"
              @click="downloadInHouseCertificate"
              data-cy="download-certificate-button"
            >
              <font-awesome-icon icon="fa-solid fa-users" />
              In-House Kosher Certificate
            </Button>

            <div class="downloads-module__download-ukd">
              <input
                type="text"
                class="downloads-module__download-ukd-input"
                title="Download LOC for single product"
                placeholder="Print by UKD ID"
                v-model="ukdId"
                @keyup.enter.prevent="downloadByUkdIdAsync"
                @keydown.enter.prevent
                data-cy="print-ukd-input"
              />
              <input
                type="image"
                class="downloads-module__download-ukd-icon"
                alt="Print by UKD ID"
                :src="printIconUrl"
                @click.prevent="downloadByUkdIdAsync"
                data-cy="print-ukd-button"
              />
            </div>
          </div>
          <div class="downloads-module__flex">
            <LoadingButton
              v-if="showScheduleBDownload"
              class="downloads-module__download-button"
              themeColor="warning"
              type="button"
              :loading="downloadingScheduleB"
              @click="downloadScheduleBAsync"
              data-cy="download-products-button"
            >
              <font-awesome-icon icon="fa-solid fa-cubes" />
              Product List
            </LoadingButton>
            <LoadingButton
              v-if="showScheduleADownload"
              class="downloads-module__download-button"
              themeColor="success"
              type="button"
              :loading="downloadingScheduleA"
              @click="downloadScheduleAAsync"
              data-cy="download-ingredients-button"
            >
              <font-awesome-icon icon="fa-solid fa-database" />
              Ingredient List
            </LoadingButton>
          </div>
        </div>
      </template>
    </UITabContainer>
  </div>
</template>

<script setup lang="ts">
import UITabContainer from "@/shared/ui/tabs/tab-container.vue";
import { computed, ref } from "vue";
import { useMainStore } from "@/stores/useMainStore";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { api } from "@/api";
import LocPickupGrid from "./LocPickupGrid.vue";
import { Button } from "@progress/kendo-vue-buttons";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import {
  IngredientsAppGroup,
  ProductAppGroup,
  ScheduleAppGroup,
} from "@/shared/access/RoleGroups";
import printIconUrl from "@/assets/img/print-icon.png";
import { EnumMerchStatus } from "@/Domain/EnumMerchStatus";
import { printLocAsync } from "@/shared/helpers/printLocHelper";
import { authorizedForLocDownloads } from "@/shared/access/IsAuthorizedForLocDownloads";
import LoadingButton from "@/shared/ui/inputs/LoadingButton.vue";

const main = useMainStore();
const notificationStore = useNotificationStore();

const isAuthorizedForLocDownloads = authorizedForLocDownloads();
const ukdId = ref<string>("");
const downloadingScheduleA = ref<boolean>(false);
const downloadingScheduleB = ref<boolean>(false);

const tabs = computed(() => {
  const tabArr = [];
  if (main.locPickup && main.locPickup.length > 0) {
    tabArr.push({
      id: "LocPickup",
      label: "Kosher Certificates Pickup",
      showIndicator: true,
    });
  }
  if (anyDownloadsAvailable.value) {
    tabArr.push({
      id: "Downloads",
      label: "Downloads",
    });
  }
  return tabArr;
});

const showScheduleADownload = computed(() => {
  return (
    main.isInAnyRole([
      ...ScheduleAppGroup.roles,
      ...IngredientsAppGroup.roles,
    ]) &&
    main.companySummary &&
    !main.companySummary.onLocHold &&
    main.companySummary.denials.every(
      (d) => !d.SchedA || d.SchedA.toUpperCase() !== "ALL"
    )
  );
});

const showScheduleBDownload = computed(() => {
  return (
    main.isInAnyRole([...ScheduleAppGroup.roles, ...ProductAppGroup.roles]) &&
    main.companySummary &&
    !main.companySummary.onLocHold &&
    main.companySummary.denials.every(
      (d) => !d.SchedB || d.SchedB.toUpperCase() !== "ALL"
    )
  );
});

const anyDownloadsAvailable = computed(() => {
  return (
    isAuthorizedForLocDownloads.value ||
    showScheduleADownload.value ||
    showScheduleBDownload.value
  );
});

const downloadInHouseCertificate = () => {
  printLocAsync({
    CompanyLogin: main.companyLogin,
    LocCalledFrom: "Vue",
    LocReportMyCompId: main.companyLogin,
    LocReportPrintOption: "InHouseCombinedProducts",
    LocReportIsFullLoc: true,
    LocReportLabelType: "InHouse",
    LocReportHeaderOption: "Company",
    LocReportSelectedIds: "",
    LocReportPlantList: "",
    LocReportIndividualPlant: false,
    LocReportDistList: "ALL",
    LocReportProductByBrand: false,
    LocReportNameBuilder: "",
    LocReportCustomZipFileName: "",
    LocReportCalledFromUkd: false,
  });
};

const downloadByUkdIdAsync = async () => {
  return handleResponsePromiseAsync(
    api.getDownload("DownloadLocByUkdId", {
      companyLogin: main.companyLogin,
      ukdId: ukdId.value,
    }),
    () => {},
    "DownloadsModule.vue",
    "downloadByUkdIdAsync",
    (error, response) => {
      notificationStore.notify({
        message:
          response?.error.message ?? "Error downloading LOC. Please try again.",
        type: NotificationTypeEnum.error,
        duration: 5000,
      });
    }
  );
};

const downloadScheduleAAsync = async function () {
  downloadingScheduleA.value = true;

  const downloadPromise = api.getDownload("ScheduleADownloadPdf", {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    plantList: "",
    sort: "LabelName",
    allCompaniesInPlant: false,
  });

  await handleResponsePromiseAsync(
    downloadPromise,
    () => {},
    "DownloadsModule.vue",
    "downloadScheduleA",
    (error) => {
      notificationStore.notify({
        type: NotificationTypeEnum.error,
        message: `Error downloading PDF file. ${error}`,
        duration: 5000,
      });
    }
  );

  downloadingScheduleA.value = false;
};

const scheduleBStatusList = [
  EnumMerchStatus[EnumMerchStatus.Certified],
  EnumMerchStatus[EnumMerchStatus.Pending],
  EnumMerchStatus[EnumMerchStatus.Terminated],
  EnumMerchStatus[EnumMerchStatus.Rejected],
  EnumMerchStatus[EnumMerchStatus.Withdrawn],
  EnumMerchStatus[EnumMerchStatus.WebApp],
];

const downloadScheduleBAsync = async function() {
  downloadingScheduleB.value = true;

  const downloadPromise = api.getDownload("ScheduleBDownloadPdf", {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    plantList: "",
    statusList: scheduleBStatusList.join(";"),
    allPlantCompanies: false,
  });

  await handleResponsePromiseAsync(
    downloadPromise,
    () => {},
    "DownloadsModule.vue",
    "downloadScheduleB",
    (error) => {
      notificationStore.notify({
        type: NotificationTypeEnum.error,
        message: `Error downloading PDF file. ${error}`,
        duration: 5000,
      });
    }
  );

  downloadingScheduleB.value = false;
};

const inHouseLocAvailable = computed(() => {
  return main.companySummary && !main.companySummary.FormatBRestricted;
});
</script>

<style scoped lang="scss">
.downloads-module {
  border: 1px solid #ddd;
  background: white;

  &__container {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    margin: 12px;
  }

  &__flex {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;

    @include media-breakpoint-up(sm) {
      flex-wrap: nowrap;
    }

    &:not(:only-child):first-child {
      border-bottom: 3px solid #ddd;
      margin-bottom: 10px;
    }
  }

  &__download-button {
    margin-bottom: 10px;
    flex-grow: 1;
    flex-basis: 50%;
  }

  &__download-ukd {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    position: relative;
    margin-bottom: 10px;
    flex-grow: 1;
    flex-basis: 50%;

    &-input {
      width: 100%;
    }

    &-icon {
      position: absolute;
      top: 10px;
      right: 12px;
      width: 14px;
    }
  }
}
</style>

<style lang="scss">
.downloads-module {
  .tab-header {
    background: #efefef;
  }
}
</style>
